<template>
  <div class="singers-page">
    <!-- ================================================================
         顶部固定栏：左侧头像+猫儿号 / 右侧进入予爱の世界按钮
         ================================================================ -->
    <div class="top-bar">
      <div class="top-bar-left">
        <div class="top-bar-avatar">
          <img v-if="topbarConfig.avatar" :src="topbarConfig.avatar" alt="头像" @error="topbarConfig.avatar = ''" />
          <div v-else class="top-bar-avatar-placeholder">🐱</div>
        </div>
        <span class="top-bar-id">{{ topbarConfig.title }}</span>
      </div>
      <button class="top-bar-enter" @click="openTopbarLink">
        <svg class="heart-icon" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
        <span>{{ topbarConfig.buttonText }}</span>
      </button>
    </div>

    <!-- 像素风格装饰边框 - 左侧 -->
    <div class="pixel-border-left">
      <div class="pixel-heart pink"></div>
      <div class="pixel-star gold"></div>
      <div class="pixel-ghost"></div>
      <div class="pixel-bear"></div>
      <div class="winged-heart"></div>
      <div class="pixel-heart blue"></div>
      <div class="pixel-star silver"></div>
      <div class="music-note"></div>
      <div class="pixel-bell"></div>
      <div class="pixel-heart lavender"></div>
      <div class="pixel-star gold"></div>
      <div class="pixel-heart mint"></div>
    </div>

    <!-- 像素风格装饰边框 - 右侧 -->
    <div class="pixel-border-right">
      <div class="pixel-star gold"></div>
      <div class="pixel-heart lavender"></div>
      <div class="music-note"></div>
      <div class="pixel-bell"></div>
      <div class="pixel-heart blue"></div>
      <div class="pixel-star silver"></div>
      <div class="winged-heart"></div>
      <div class="pixel-ghost"></div>
      <div class="pixel-heart pink"></div>
      <div class="pixel-bear"></div>
      <div class="pixel-star gold"></div>
      <div class="pixel-heart mint"></div>
    </div>

    <!-- 背景装饰 -->
    <div class="bg-decorations" ref="bgDecorations"></div>

    <!-- ================================================================
         Hero 区：开场动画 + 四宫格手风琴导航
         ================================================================ -->
    <section class="hero-section">
      <!-- 背景光晕 -->
      <div class="bg-decoration"></div>

      <!-- 蕾丝边角 -->
      <div class="lace-frame">
        <div class="lace-corner top-left"></div>
        <div class="lace-corner top-right"></div>
        <div class="lace-corner bottom-right"></div>
        <div class="lace-corner bottom-left"></div>
      </div>

      <!-- 星星系统 -->
      <div class="star-container" ref="starContainer"></div>
      <div class="center-star">⭐</div>
      <div class="star-wave" style="animation-delay: 0.2s;"></div>
      <div class="star-wave" style="animation-delay: 0.5s;"></div>

      <!-- 标题（上移至顶部区域） -->
      <div class="dream-title">
        <h1 class="pixel-text">欢迎来到予爱の世界</h1>
        <div class="dream-subtitle">✦ 星光闪耀 · 爱予你 ✦</div>
      </div>

      <!-- 四宫格手风琴导航 -->
      <div class="accordion-shell">
        <!-- 手风琴顶部像素小猫跑动 -->
        <PixelCatRunner :speed="1.2" :cat-type="0" :height="32" />
        <!-- 装饰光斑 -->
        <div class="acc-glow acc-glow-1"></div>
        <div class="acc-glow acc-glow-2"></div>

        <div class="accordion pixel-acc">
          <!-- 面板1：页面信息（默认展开） -->
          <div
            class="accordion-item acc-config"
            :class="{ active: activeAccordion === 'config' }"
            @click="selectAccordion('config')"
          >
            <div class="ai-bg" :style="accordionBgStyles[0]"></div>
            <div class="ai-side">
              <span class="ai-badge">🏰</span>
              <span class="ai-vertical-tit">予爱の世界</span>
            </div>
            <div class="ai-content">
              <div class="ai-content-inner">
                <div class="ai-head">
                  <span class="ai-kicker">✦ 走进 · 梦幻空间 ✦</span>
                  <h3 class="ai-tit">{{ config.title }}</h3>
                </div>
                <p class="ai-desc">{{ config.description }}</p>
                <div class="ai-meta">
                  <span class="ai-tag">🕗 营业时间 8:00-24:00</span>
                  <span class="ai-tag ai-tag-soft">🎧 排档直播</span>
                </div>
                <button class="ai-live-btn pixel-btn" @click.stop="openLiveRoom">
                  <span class="pulse-dot"></span>
                  <span>{{ config.buttonText }}</span>
                  <span class="btn-arrow">➤</span>
                </button>
              </div>
            </div>
            <div class="ai-footer">
              <span class="ai-footer-icon">🏰</span>
              <span class="ai-footer-text">予爱の世界 · 梦幻空间</span>
            </div>
          </div>

          <!-- 面板2：签到 -->
          <div
            class="accordion-item acc-sign"
            :class="{ active: activeAccordion === 'sign' }"
            @click="selectAccordion('sign')"
          >
            <div class="ai-bg" :style="accordionBgStyles[1]"></div>
            <div class="ai-side">
              <span class="ai-badge">📅</span>
              <span class="ai-vertical-tit">签到</span>
            </div>
            <div class="ai-content">
              <div class="ai-content-inner">
                <div class="ai-head">
                  <span class="ai-kicker">✦ 每日 · 坚持 ✦</span>
                  <h3 class="ai-tit">签到打卡</h3>
                </div>
                <p class="ai-desc">每日签到累积天数，兑换专属福利与惊喜好礼</p>
                <div class="ai-meta">
                  <span class="ai-tag">🎁 兑礼规则</span>
                  <span class="ai-tag ai-tag-soft">🔥 连续签到</span>
                </div>
                <button class="ai-go-btn pixel-btn" @click.stop="gotoModule('sign')">
                  <span>立即签到</span>
                  <span class="btn-arrow">➤</span>
                </button>
              </div>
            </div>
            <div class="ai-footer">
              <span class="ai-footer-icon">📅</span>
              <span class="ai-footer-text">签到打卡 · 每日坚持兑好礼</span>
            </div>
          </div>

          <!-- 面板3：试音抽卡 -->
          <div
            class="accordion-item acc-draw"
            :class="{ active: activeAccordion === 'draw' }"
            @click="selectAccordion('draw')"
          >
            <div class="ai-bg" :style="accordionBgStyles[2]"></div>
            <div class="ai-side">
              <span class="ai-badge">🎴</span>
              <span class="ai-vertical-tit">抽卡</span>
            </div>
            <div class="ai-content">
              <div class="ai-content-inner">
                <div class="ai-head">
                  <span class="ai-kicker">✦ 试音 · 探索 ✦</span>
                  <h3 class="ai-tit">试音抽卡</h3>
                </div>
                <p class="ai-desc">抽取试音卡片，聆听每一位歌手的声音魔法</p>
                <div class="ai-meta">
                  <span class="ai-tag">✨ 声音盲盒</span>
                  <span class="ai-tag ai-tag-soft">🎵 每日限抽</span>
                </div>
                <button class="ai-go-btn pixel-btn" @click.stop="gotoModule('draw')">
                  <span>开始抽卡</span>
                  <span class="btn-arrow">➤</span>
                </button>
              </div>
            </div>
            <div class="ai-footer">
              <span class="ai-footer-icon">🎴</span>
              <span class="ai-footer-text">试音抽卡 · 聆听声音魔法</span>
            </div>
          </div>

          <!-- 面板4：歌手信息 -->
          <div
            class="accordion-item acc-singer"
            :class="{ active: activeAccordion === 'singer' }"
            @click="selectAccordion('singer')"
          >
            <div class="ai-bg" :style="accordionBgStyles[3]"></div>
            <div class="ai-side">
              <span class="ai-badge">🎤</span>
              <span class="ai-vertical-tit">歌手</span>
            </div>
            <div class="ai-content">
              <div class="ai-content-inner">
                <div class="ai-head">
                  <span class="ai-kicker">✦ 风采 · 歌单 ✦</span>
                  <h3 class="ai-tit">歌手信息</h3>
                </div>
                <p class="ai-desc">浏览歌手风采，尽享精彩歌单与动人旋律</p>
                <div class="ai-meta">
                  <span class="ai-tag">🎶 歌手歌单</span>
                  <span class="ai-tag ai-tag-soft">💫 唱见推荐</span>
                </div>
                <button class="ai-go-btn pixel-btn" @click.stop="gotoModule('singer')">
                  <span>浏览歌手</span>
                  <span class="btn-arrow">➤</span>
                </button>
              </div>
            </div>
            <div class="ai-footer">
              <span class="ai-footer-icon">🎤</span>
              <span class="ai-footer-text">歌手信息 · 尽享精彩歌单</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 下滑提示 -->
      <div class="scroll-hint">下滑探索更多 ✦</div>
    </section>

    <!-- ================================================================
         内容区：签到 / 抽卡 / 歌手列表
         ================================================================ -->
    <section class="content-section">
      <div class="main-container">
        <div class="lace-wrapper">
          <div class="inner-content">

            <!-- 像素分隔线 -->
            <div class="pixel-divider"></div>

            <!-- 签到模块 -->
            <div class="module-anchor" ref="moduleSign"></div>
            <SignModule
              :sign-rank-name="signRankName"
              :sign-info="signInfo"
              :user-nickname="userNickname"
              :target-list="targetList"
              :sign-point-number="signPointNumber"
              @show-toast="showToast"
              @sign-success="onSignSuccess"
              @exchange-success="onExchangeSuccess"
            />

            <!-- 每日抽卡模块 -->
            <div class="module-anchor" ref="moduleDraw"></div>
            <DrawModule
              @show-toast="showToast"
            />

            <!-- 像素分隔线 -->
            <div class="pixel-divider"></div>

            <!-- 歌手列表 -->
            <div class="module-anchor" ref="moduleSinger"></div>
            <SingerList
              :all-singers="allSingers"
              :loading="loading"
              @open-songlist="openSonglist"
            />

          </div>
        </div>
      </div>
    </section>

    <!-- Toast -->
    <div class="toast" :class="{ show: toastVisible }" ref="toast">{{ toastMessage }}</div>

    <!-- 跨设备分享 - 固定左下角 -->
    <button class="share-link-btn" @click="copyShareLink" title="跨设备分享链接">
      <svg class="share-phone-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
        <line x1="12" y1="18" x2="12.01" y2="18"/>
      </svg>
      <span>跨设备共享</span>
    </button>

    <!-- 歌单弹窗 - 遮罩层 -->
    <div class="songlist-overlay" :class="{ active: showSonglist }" @click="closeSonglist">
      <!-- 歌单弹窗 - 模态框 -->
      <div class="songlist-modal" @click.stop>
        <div class="songlist-header">
          <button class="songlist-close-btn" @click="closeSonglist">✕</button>
          <div class="songlist-singer-avatar">
            <img v-if="currentSonglistSinger && currentSonglistSinger.avatar" :src="currentSonglistSinger.avatar" :alt="currentSonglistSinger.name" @error="hideEl" />
            <div v-else-if="currentSonglistSinger" class="songlist-avatar-placeholder">{{ currentSonglistSinger.name.charAt(0) }}</div>
          </div>
          <div class="songlist-title">{{ currentSonglistSinger ? currentSonglistSinger.name : '' }}</div>
          <div class="songlist-subtitle">{{ songlistSubtitle }}</div>
        </div>
        <div class="songlist-search-bar">
          <svg class="songlist-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
          </svg>
          <input type="text" v-model="songlistSearchKeyword" placeholder="搜索原唱歌手/歌名" class="songlist-search-input" />
        </div>
        <div class="songlist-body">
          <template v-if="currentSonglistSinger && filteredSonglistSongs.length > 0">
            <div v-for="(group, gi) in songlistSongGroups" :key="gi">
              <div class="song-group-header">{{ group.label }} ({{ group.songs.length }})</div>
              <div v-for="(song, si) in group.songs" :key="song.id" class="song-item" :style="{ animationDelay: Math.min(si * 0.02, 0.5) + 's' }">
                <span class="song-index">{{ song._index }}</span>
                <img v-if="song.image" class="song-image" :src="song.image" alt="" @error="hideEl" />
                <span class="song-title">{{ song.title }}</span>
                <span v-if="song.note && song.note.trim()" class="song-note">{{ song.note.trim() }}</span>
              </div>
            </div>
          </template>
          <div v-else-if="currentSonglistSinger" class="empty-state">
            <div class="empty-icon">🔍</div>
            <div class="empty-text">没有找到匹配的歌曲</div>
          </div>
        </div>
        <div class="songlist-footer" v-if="currentSonglistSinger">
          共 {{ (currentSonglistSinger.songs || []).length }} 首歌曲
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import API from '../api/api';
import SignModule from '../components/SignModule.vue';
import DrawModule from '../components/DrawModule.vue';
import SingerList from '../components/SingerList.vue';
import PixelCatRunner from '../components/PixelCatRunner.vue';
import { sortSongsForDisplay, groupSongsForView, splitTags } from '../utils/songUtils';

export default {
  name: 'SingersPage',
  components: {
    SignModule,
    DrawModule,
    SingerList,
    PixelCatRunner,
  },
  data() {
    return {
      // 手风琴当前激活面板
      activeAccordion: 'config',
      // 手风琴各面板背景（随机歌手头像，高透明度）
      accordionBgStyles: [
        {},
        {},
        {},
        {},
      ],
      allSingers: [],
      topbarConfig: {
        avatar: localStorage.getItem('topbar_avatar') || '',
        title: '予爱の世界',
        buttonText: '点击进入予爱の世界',
        buttonUrl: 'https://fm.missevan.com/live/869109850',
      },
      signInfo: null,
      userNickname: '',
      config: {
        title: '予爱の世界',
        subtitle: '予爱悠悠，声声为你',
        description: '本页面所有歌手均在予爱の世界排档直播，营业时间 8:00-24:00，欢迎前来收听 o(*≧▽≦*)ツ',
        liveRoomUrl: 'https://fm.missevan.com/live/869109850',
        buttonText: '进入猫耳FM直播间',
      },
      signRankName: '周榜',
      targetList: [],
      signPointNumber: 0,
      loading: true,
      toastVisible: false,
      toastMessage: '',
      toastTimer: null,
      // 歌单弹窗
      showSonglist: false,
      currentSonglistSinger: null,
      songlistSearchKeyword: '',
    };
  },
  computed: {
    // 歌单弹窗：按曲风默认分组，支持搜索过滤
    songlistSortedSongs() {
      if (!this.currentSonglistSinger) return [];
      return sortSongsForDisplay(this.currentSonglistSinger.songs || []);
    },
    filteredSonglistSongs() {
      const kw = this.songlistSearchKeyword.trim().toLowerCase();
      if (!kw) return this.songlistSortedSongs;
      return this.songlistSortedSongs.filter((song) => {
        const titleMatch = (song.title || '').toLowerCase().includes(kw);
        const originalSinger = (song.originalSinger || '').toLowerCase();
        const originalSingerMatch = originalSinger.includes(kw);
        return titleMatch || originalSingerMatch;
      });
    },
    songlistSongGroups() {
      return groupSongsForView(this.filteredSonglistSongs, 'genre');
    },
    songlistSubtitle() {
      if (!this.currentSonglistSinger) return '';
      const tags = splitTags(this.currentSonglistSinger.genre);
      const songCount = (this.currentSonglistSinger.songs || []).length;
      return songCount + ' 首歌曲' + (tags.length ? ' · ' + tags.join(' / ') : '');
    },
  },
  methods: {
    // ================================================================
    // 开场动画星星
    // ================================================================
    createStars() {
      const container = this.$refs.starContainer;
      if (!container) return;
      const stars = ['✦', '✧', '⭐', '✴', '✵'];
      for (let i = 0; i < 30; i++) {
        const star = document.createElement('div');
        star.className = 'star-item';
        star.textContent = stars[Math.floor(Math.random() * stars.length)];
        star.style.left = `${Math.random() * 100}%`;
        star.style.top = `${Math.random() * 100}%`;
        star.style.fontSize = `${16 + Math.random() * 20}px`;
        star.style.animationDelay = `${Math.random() * 2}s`;
        star.style.color = `hsl(${Math.random() * 60 + 200}, 70%, 70%)`;
        container.appendChild(star);
      }
    },

    // ================================================================
    // 手风琴导航
    // ================================================================
    selectAccordion(key) {
      this.activeAccordion = key;
    },
    gotoModule(key) {
      const refMap = { sign: 'moduleSign', draw: 'moduleDraw', singer: 'moduleSinger' };
      const el = this.$refs[refMap[key]];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    // 从歌手列表中随机分配头像到手风琴背景
    assignAccordionBgImages(singers) {
      const validSingers = singers.filter(s => s.avatar);
      if (validSingers.length === 0) return;
      const shuffled = [...validSingers].sort(() => Math.random() - 0.5);
      const styles = [];
      // 4个面板，每个面板使用2-3张不同歌手头像拼成背景
      for (let i = 0; i < 4; i++) {
        const imgs = [];
        for (let j = 0; j < 3; j++) {
          const idx = (i * 3 + j) % shuffled.length;
          imgs.push(shuffled[idx].avatar);
        }
        styles.push({
          backgroundImage: imgs.map(url => `url(${url})`).join(', '),
          backgroundSize: 'cover, cover, cover',
          backgroundPosition: 'center, center, center',
          backgroundBlendMode: 'overlay, overlay, normal',
          opacity: 0.1,
        });
      }
      this.accordionBgStyles = styles;
    },

    async loadData() {
      try {
        // 同时获取配置、歌手列表和用户信息
        const [cfg, singers, userInfo] = await Promise.all([
          API.getConfig(),
          API.getSingers(),
          API.getUserInfo(),
        ]);

        if (cfg && cfg.topbar_config) {
          this.topbarConfig = Object.assign({}, this.topbarConfig, cfg.topbar_config);
          if (this.topbarConfig.avatar) {
            localStorage.setItem('topbar_avatar', this.topbarConfig.avatar);
          }
        }
        if (cfg && cfg.page_config) {
          this.config = Object.assign({}, this.config, cfg.page_config);
        }
        if (cfg && cfg.sign_time_range_config) {
          this.signRankName = cfg.sign_time_range_config.signName || '周榜';
          this.targetList = cfg.sign_time_range_config.targetList || [];
        }
        if (userInfo) {
          if (userInfo.nickname) {
            this.userNickname = userInfo.nickname;
          }
          if (typeof userInfo.signPointNumber === 'number') {
            this.signPointNumber = userInfo.signPointNumber;
          }
        }
        if (singers && singers.length) {
          this.allSingers = singers.sort((a, b) => {
            const oa = typeof a.order === 'number' ? a.order : 999;
            const ob = typeof b.order === 'number' ? b.order : 999;
            if (oa !== ob) return oa - ob;
            return a.name.localeCompare(b.name, 'zh-Hans-CN-u-co-pinyin');
          });
          this.assignAccordionBgImages(this.allSingers);
        }
      } catch (e) {
        console.error('加载数据失败:', e);
        this.showToast('数据加载失败，请稍后重试');
      } finally {
        this.loading = false;
      }
    },

    openLiveRoom() {
      window.open(this.config.liveRoomUrl, '_blank');
    },

    openTopbarLink() {
      window.open(this.topbarConfig.buttonUrl, '_blank');
    },

    copyShareLink() {
      const deviceId = localStorage.getItem('sign_device_id');
      if (!deviceId) return;
      const url = window.location.origin + window.location.pathname + '?deviceId=' + deviceId;
      navigator.clipboard.writeText(url).then(() => {
        this.showToast('分享链接已复制到剪贴板');
      }).catch(() => {
        this.showToast('复制失败，请手动复制');
      });
    },

    onSignSuccess() {
      // 签到成功后，重新获取用户信息以更新签到点数
      API.getUserInfo().then((userInfo) => {
        if (userInfo && typeof userInfo.signPointNumber === 'number') {
          this.signPointNumber = userInfo.signPointNumber;
        }
      }).catch((e) => {
        console.error('获取用户信息失败:', e);
      });
    },

    onExchangeSuccess(consumedPoints) {
      this.signPointNumber -= consumedPoints;
    },

    showToast(msg) {
      this.toastMessage = msg;
      this.toastVisible = true;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toastVisible = false;
      }, 2500);
    },

    // ================================================================
    // 歌单弹窗
    // ================================================================
    openSonglist(singer) {
      this.currentSonglistSinger = singer;
      this.songlistSearchKeyword = '';
      this.showSonglist = true;
      document.body.style.overflow = 'hidden';
    },

    closeSonglist() {
      this.showSonglist = false;
      this.currentSonglistSinger = null;
      this.songlistSearchKeyword = '';
      document.body.style.overflow = '';
    },

    onKeydown(e) {
      if (e.key === 'Escape' && this.showSonglist) {
        this.closeSonglist();
      }
    },

    hideEl(e) {
      const el = e.target;
      if (el && el.tagName === 'IMG') {
        el.style.display = 'none';
      }
    },

    // ================================================================
    // 背景装饰
    // ================================================================
    initBackgroundDecorations() {
      const container = this.$refs.bgDecorations;
      if (!container) return;

      // 云朵
      const cloudPositions = [
        { top: '5%', left: '8%', size: 70, delay: 0 },
        { top: '12%', left: '75%', size: 90, delay: 2 },
        { top: '45%', left: '5%', size: 60, delay: 4 },
        { top: '70%', left: '80%', size: 80, delay: 1 },
        { top: '85%', left: '15%', size: 65, delay: 3 },
      ];

      cloudPositions.forEach((pos) => {
        const cloud = document.createElement('div');
        cloud.className = 'bg-cloud';
        cloud.style.top = pos.top;
        cloud.style.left = pos.left;
        cloud.style.animationDelay = pos.delay + 's';
        cloud.innerHTML = '<svg width="' + pos.size + '" height="' + (pos.size * 0.6) + '" viewBox="0 0 100 60">'
          + '<ellipse cx="30" cy="40" rx="25" ry="18" fill="white" opacity="0.85"/>'
          + '<ellipse cx="55" cy="35" rx="30" ry="22" fill="white" opacity="0.95"/>'
          + '<ellipse cx="75" cy="42" rx="20" ry="15" fill="white" opacity="0.85"/>'
          + '</svg>';
        container.appendChild(cloud);
      });

      // 气泡
      for (let i = 0; i < 15; i++) {
        const bubble = document.createElement('div');
        bubble.className = 'bubble';
        const size = 15 + Math.random() * 35;
        bubble.style.width = size + 'px';
        bubble.style.height = size + 'px';
        bubble.style.left = Math.random() * 100 + '%';
        bubble.style.top = Math.random() * 100 + '%';
        bubble.style.animationDelay = Math.random() * 8 + 's';
        bubble.style.animationDuration = (8 + Math.random() * 6) + 's';
        container.appendChild(bubble);
      }

      // 星星
      for (let i = 0; i < 25; i++) {
        const sparkle = document.createElement('div');
        sparkle.className = 'sparkle';
        sparkle.style.left = Math.random() * 100 + '%';
        sparkle.style.top = Math.random() * 100 + '%';
        sparkle.style.animationDelay = Math.random() * 4 + 's';
        sparkle.style.animationDuration = (3 + Math.random() * 3) + 's';
        container.appendChild(sparkle);
      }

      // 漂浮爱心
      const hearts = ['💕', '💙', '🤍', '✨', '🌸', '💝', '💖', '💗'];
      for (let i = 0; i < 12; i++) {
        const heart = document.createElement('div');
        heart.className = 'floating-heart';
        heart.textContent = hearts[Math.floor(Math.random() * hearts.length)];
        heart.style.left = Math.random() * 100 + '%';
        heart.style.animationDelay = Math.random() * 10 + 's';
        heart.style.animationDuration = (10 + Math.random() * 8) + 's';
        container.appendChild(heart);
      }
    },
  },
  mounted() {
    if (!sessionStorage.getItem('_r')) {
      sessionStorage.setItem('_r', '1');
      window.location.reload();
      return;
    }
    sessionStorage.removeItem('_r');
    document.body.style.transition = '';
    document.body.style.opacity = '';
    this.createStars();
    this.initBackgroundDecorations();
    this.loadData();
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy() {
    clearTimeout(this.toastTimer);
    document.removeEventListener('keydown', this.onKeydown);
    document.body.style.overflow = '';
  },
};
</script>

<style>
/* 全局样式已在 src/assets/global.css 中定义 */
</style>

<style scoped>
.singers-page {
  font-family: 'Noto Sans SC', 'Quicksand', -apple-system, BlinkMacSystemFont, sans-serif;
  color: var(--text-primary);
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  padding-top: 56px;
  background:
    repeating-linear-gradient(
      90deg,
      transparent,
      transparent 30px,
      rgba(200, 216, 240, 0.3) 30px,
      rgba(200, 216, 240, 0.3) 60px
    ),
    repeating-linear-gradient(
      0deg,
      transparent,
      transparent 30px,
      rgba(200, 216, 240, 0.3) 30px,
      rgba(200, 216, 240, 0.3) 60px
    ),
    linear-gradient(135deg,
      #E8F0F8 0%,
      #C8D8F0 25%,
      #E8F0F8 50%,
      #D5E5F8 75%,
      #E8F0F8 100%
    );
  background-attachment: fixed;
  scroll-snap-type: y proximity;
}

/* ================================================================
   顶部固定栏
   ================================================================ */
.top-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  background: linear-gradient(135deg, #7B93DB, #9B7FD4);
  box-shadow: 0 2px 12px rgba(74, 90, 138, 0.35);
  /* 像素风底部边框 */
  border-bottom: 3px solid var(--pixel-dark);
  box-shadow:
    0 2px 0 var(--pixel-ice),
    0 4px 0 var(--pixel-blue),
    0 6px 12px rgba(74, 90, 138, 0.3);
}

.top-bar-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.top-bar-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.8);
  background: linear-gradient(135deg, #a8c8ff, #c8b0ff);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.top-bar-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.top-bar-avatar-placeholder {
  font-size: 20px;
}

.top-bar-id {
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
}

.top-bar-enter {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.18);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.3s ease, transform 0.3s ease;
  white-space: nowrap;
  box-shadow: inset 0 2px 0 rgba(255, 255, 255, 0.2);
}

.top-bar-enter:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

.top-bar-enter .heart-icon {
  flex-shrink: 0;
  color: #ffd1e0;
}

@media (max-width: 480px) {
  .top-bar {
    height: 52px;
    padding: 0 10px;
  }
  .top-bar-enter {
    font-size: 12px;
    padding: 7px 10px;
  }
  .top-bar-enter .heart-icon {
    width: 14px;
    height: 14px;
  }
  .top-bar-avatar {
    width: 32px;
    height: 32px;
  }
  .top-bar-id {
    font-size: 13px;
  }
}

/* ================================================================
   像素风格装饰边框
   ================================================================ */
.pixel-border-left,
.pixel-border-right {
  position: fixed;
  top: 0;
  bottom: 0;
  width: 100px;
  pointer-events: none;
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
  padding: 30px 15px;
}

.pixel-border-left { left: 0; background: linear-gradient(90deg, rgba(139, 181, 232, 0.2), transparent); }
.pixel-border-right { right: 0; background: linear-gradient(-90deg, rgba(139, 181, 232, 0.2), transparent); }

.pixel-heart {
  width: 28px;
  height: 28px;
  position: relative;
  animation: pixelHeartBeat 2s ease-in-out infinite;
  filter: drop-shadow(0 0 10px var(--glow-pink));
}

.pixel-heart::before {
  content: '❤';
  font-size: 24px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.pixel-heart.pink { color: #FFB8D0; }
.pixel-heart.blue { color: #8BB5E8; }
.pixel-heart.lavender { color: #A8B8D8; animation-delay: 0.5s; }
.pixel-heart.mint { color: #B8E8D8; animation-delay: 1s; }

@keyframes pixelHeartBeat {
  0%, 100% { transform: scale(1) rotate(0deg); }
  10% { transform: scale(1.2) rotate(-5deg); }
  20% { transform: scale(1.1) rotate(5deg); }
  30% { transform: scale(1.15) rotate(-3deg); }
  40% { transform: scale(1.05) rotate(3deg); }
  50% { transform: scale(1.1) rotate(0deg); }
}

.pixel-star {
  width: 18px;
  height: 18px;
  position: relative;
  animation: starTwinkleEnhanced 2.5s ease-in-out infinite;
}

.pixel-star::before {
  content: '✦';
  font-size: 16px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.pixel-star.gold { color: #FFD890; animation-delay: 0.3s; }
.pixel-star.silver { color: #C8D8E8; animation-delay: 0.7s; }

@keyframes starTwinkleEnhanced {
  0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(0.8) rotate(0deg); filter: drop-shadow(0 0 5px rgba(255, 216, 144, 0.5)); }
  50% { opacity: 1; transform: translate(-50%, -50%) scale(1.2) rotate(180deg); filter: drop-shadow(0 0 15px rgba(255, 216, 144, 1)); }
}

.pixel-ghost {
  width: 36px;
  height: 36px;
  position: relative;
  animation: ghostFloatEnhanced 4s ease-in-out infinite;
}

.pixel-ghost::before {
  content: '👻';
  font-size: 32px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  filter: drop-shadow(0 0 12px rgba(168, 184, 232, 0.8));
}

@keyframes ghostFloatEnhanced {
  0%, 100% { transform: translateY(0) rotate(-8deg); opacity: 0.9; }
  25% { transform: translateY(-15px) rotate(8deg); opacity: 1; }
  50% { transform: translateY(-20px) rotate(-8deg); opacity: 0.95; }
  75% { transform: translateY(-10px) rotate(8deg); opacity: 1; }
}

.pixel-bear {
  width: 44px;
  height: 44px;
  position: relative;
  animation: bearBounceEnhanced 3s ease-in-out infinite;
}

.pixel-bear::before {
  content: '🐻';
  font-size: 40px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

@keyframes bearBounceEnhanced {
  0%, 100% { transform: scale(1) translateY(0) rotate(0deg); }
  15% { transform: scale(1.1) translateY(-10px) rotate(-5deg); }
  30% { transform: scale(0.95) translateY(5px) rotate(5deg); }
}

.pixel-bell {
  width: 32px;
  height: 32px;
  position: relative;
  animation: bellSwingEnhanced 2s ease-in-out infinite;
}

.pixel-bell::before {
  content: '🔔';
  font-size: 28px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

@keyframes bellSwingEnhanced {
  0%, 100% { transform: rotate(-12deg); }
  20% { transform: rotate(12deg); }
  40% { transform: rotate(-10deg); }
  60% { transform: rotate(10deg); }
}

.winged-heart {
  width: 32px;
  height: 32px;
  position: relative;
  animation: wingedHeartFly 3s ease-in-out infinite;
}

.winged-heart::before {
  content: '💝';
  font-size: 28px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

@keyframes wingedHeartFly {
  0%, 100% { transform: translateY(0) translateX(0) scale(1); filter: drop-shadow(0 0 8px rgba(255, 182, 208, 0.8)); }
  50% { transform: translateY(-18px) translateX(-5px) scale(1.05); filter: drop-shadow(0 0 12px rgba(255, 182, 208, 0.9)); }
}

.music-note {
  width: 28px;
  height: 28px;
  position: relative;
  animation: musicNoteFloat 3.5s ease-in-out infinite;
}

.music-note::before {
  content: '🎵';
  font-size: 24px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

@keyframes musicNoteFloat {
  0%, 100% { transform: translateY(0) rotate(0deg) scale(1); opacity: 0.8; }
  50% { transform: translateY(-15px) rotate(-10deg) scale(1.05); opacity: 0.9; }
}

/* ================================================================
   背景装饰层
   ================================================================ */
.bg-decorations {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

.bg-cloud {
  position: absolute;
  opacity: 0.65;
  animation: cloudFloatEnhanced 30s ease-in-out infinite;
  filter: drop-shadow(0 6px 15px rgba(139, 181, 232, 0.4));
}

.bg-cloud svg { display: block; }

@keyframes cloudFloatEnhanced {
  0%, 100% { transform: translateX(0) translateY(0) scale(1); opacity: 0.6; }
  50% { transform: translateX(-40px) translateY(10px) scale(0.95); opacity: 0.65; }
}

.bubble {
  position: absolute;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.95), rgba(139, 181, 232, 0.4), rgba(168, 184, 232, 0.3));
  box-shadow: inset 0 0 25px rgba(255, 255, 255, 0.9), 0 0 25px rgba(139, 181, 232, 0.5);
  animation: bubbleFloatEnhanced 10s ease-in-out infinite;
}

@keyframes bubbleFloatEnhanced {
  0%, 100% { transform: translateY(0) scale(1); opacity: 0.6; filter: hue-rotate(0deg); }
  50% { transform: translateY(-60px) scale(1.1); opacity: 0.75; filter: hue-rotate(60deg); }
}

.sparkle {
  position: absolute;
  width: 10px;
  height: 10px;
  background: white;
  border-radius: 50%;
  animation: sparklePulseEnhanced 4s ease-in-out infinite;
  box-shadow: 0 0 10px rgba(255, 255, 255, 1), 0 0 20px rgba(139, 181, 232, 0.9);
}

@keyframes sparklePulseEnhanced {
  0%, 100% { transform: scale(0) rotate(0deg); opacity: 0; }
  50% { transform: scale(1.5) rotate(180deg); opacity: 1; }
}

.floating-heart {
  position: absolute;
  font-size: 18px;
  animation: heartFloatEnhanced 12s ease-in-out infinite;
  opacity: 0;
  filter: drop-shadow(0 3px 10px rgba(255, 182, 208, 0.7));
}

@keyframes heartFloatEnhanced {
  0% { transform: translateY(100vh) scale(0.4) rotate(0deg); opacity: 0; filter: hue-rotate(0deg); }
  15% { opacity: 0.8; }
  50% { transform: translateY(40vh) scale(0.9) rotate(180deg); opacity: 0.5; filter: hue-rotate(90deg); }
  100% { transform: translateY(-30px) scale(1.4) rotate(360deg); opacity: 0; filter: hue-rotate(180deg); }
}

/* ================================================================
   Hero 区：开场动画
   ================================================================ */
.hero-section {
  position: relative;
  min-height: 100vh;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 40px 24px;
}

/* 背景光晕 */
.bg-decoration {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background:
    radial-gradient(ellipse at 25% 25%, rgba(255,216,144,0.2), transparent 50%),
    radial-gradient(ellipse at 75% 75%, rgba(139,181,232,0.2), transparent 50%);
  animation: bgPulse 3.5s ease-in-out infinite;
}

@keyframes bgPulse {
  0%, 100% { opacity: 0.85; }
  50% { opacity: 1; }
}

/* 星星系统 */
.star-container {
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
}

.star-item {
  position: absolute;
  opacity: 0;
  animation: starTwinkle 2.5s ease-out forwards;
}

@keyframes starTwinkle {
  0% {
    opacity: 0;
    transform: scale(0) rotate(0deg);
  }
  30% {
    opacity: 1;
    transform: scale(1.3) rotate(90deg);
  }
  60% {
    opacity: 0.9;
    transform: scale(1) rotate(180deg);
  }
  100% {
    opacity: 0.4;
    transform: scale(0.7) rotate(270deg);
  }
}

/* 中心大星星 */
.center-star {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 5;
  font-size: 100px;
  opacity: 0;
  animation: centerStarPulse 2.5s ease-out forwards;
  filter: drop-shadow(0 0 30px rgba(255,216,144,0.9));
}

@keyframes centerStarPulse {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.2) rotate(-180deg);
    filter: drop-shadow(0 0 5px rgba(255,216,144,0.3));
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.2) rotate(0deg);
    filter: drop-shadow(0 0 50px rgba(255,216,144,1));
  }
  75% {
    transform: translate(-50%, -50%) scale(0.95) rotate(30deg);
  }
  100% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(1.5) rotate(60deg);
    filter: drop-shadow(0 0 70px rgba(255,216,144,0));
  }
}

/* 星光波纹 */
.star-wave {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255,216,144,0.3), transparent);
  opacity: 0;
  animation: starWaveExpand 2.5s ease-out forwards;
}

@keyframes starWaveExpand {
  0% {
    width: 30px;
    height: 30px;
    opacity: 0.8;
  }
  100% {
    width: 450px;
    height: 450px;
    opacity: 0;
  }
}

/* 蕾丝边框 */
.lace-frame {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  opacity: 0;
  animation: laceAppear 1.5s ease-out 0.5s forwards;
}

.lace-corner {
  position: absolute;
  width: 110px;
  height: 110px;
  background-image: url("data:image/svg+xml,%3Csvg width='110' height='110' viewBox='0 0 110 110' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0,55 Q18,38 36,55 T72,55 Q90,38 110,55' stroke='%23B8C5E8' stroke-width='2' fill='none' opacity='0.7'/%3E%3Ccircle cx='18' cy='46' r='3.5' fill='%23FFD6EC' opacity='0.8'/%3E%3Ccircle cx='55' cy='46' r='3.5' fill='%238BB5E8' opacity='0.8'/%3E%3Ccircle cx='92' cy='46' r='3.5' fill='%23D5C8F0' opacity='0.8'/%3E%3C/svg%3E");
  background-size: contain;
  background-repeat: no-repeat;
}

.lace-corner.top-left { top: 18px; left: 18px; }
.lace-corner.top-right { top: 18px; right: 18px; transform: scaleX(-1); }
.lace-corner.bottom-right { bottom: 18px; right: 18px; transform: scale(-1); }
.lace-corner.bottom-left { bottom: 18px; left: 18px; transform: scaleY(-1); }

@keyframes laceAppear {
  0% { opacity: 0; transform: scale(0.88); }
  100% { opacity: 0.75; transform: scale(1); }
}

/* 标题浮现（上移至顶部） */
.dream-title {
  position: relative;
  z-index: 15;
  text-align: center;
  pointer-events: none;
  opacity: 0;
  margin-top: -6vh;
  animation: titleReveal 2s ease-out 0.9s forwards;
}

@keyframes titleReveal {
  0% {
    opacity: 0;
    transform: scale(0.85);
    filter: blur(18px);
  }
  60% {
    opacity: 0.9;
    transform: scale(1.03);
    filter: blur(2px);
  }
  100% {
    opacity: 1;
    transform: scale(1);
    filter: blur(0);
  }
}

.dream-title h1 {
  font-family: 'ZCOOL KuaiLe', cursive;
  font-size: clamp(28px, 6vw, 60px);
  color: var(--pixel-dark);
  text-shadow:
    0 4px 20px rgba(139,181,232,0.6),
    0 8px 40px rgba(255,182,208,0.4);
  letter-spacing: 4px;
  position: relative;
  white-space: nowrap;
}

.dream-title h1::before,
.dream-title h1::after {
  content: '✦';
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  font-size: 22px;
  color: var(--dream-lavender);
  animation: starTwinkleSmall 2s ease-in-out infinite;
}

.dream-title h1::before { left: -36px; }
.dream-title h1::after { right: -36px; animation-delay: 0.4s; }

@keyframes starTwinkleSmall {
  0%, 100% { opacity: 0.5; transform: translateY(-50%) scale(0.85); }
  50% { opacity: 1; transform: translateY(-50%) scale(1.1); }
}

.dream-subtitle {
  font-size: clamp(14px, 2.5vw, 22px);
  color: #6B7D9F;
  margin-top: 16px;
  letter-spacing: 3px;
  opacity: 0;
  animation: subtitleFade 1.5s ease-out 1.5s forwards;
}

@keyframes subtitleFade {
  0% { opacity: 0; transform: translateY(15px); }
  100% { opacity: 0.88; transform: translateY(0); }
}

/* ================================================================
   四宫格手风琴导航 - 全新设计
   ================================================================ */
.accordion-shell {
  position: relative;
  z-index: 20;
  width: 100%;
  max-width: 940px;
  margin-top: 5vh;
  opacity: 0;
  animation: accordionAppear 1.2s ease-out 1.6s forwards;
  padding: 0 8px;
}

@keyframes accordionAppear {
  0% { opacity: 0; transform: translateY(40px); }
  100% { opacity: 1; transform: translateY(0); }
}

/* 装饰光斑 */
.acc-glow {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(70px);
  z-index: 0;
}
.acc-glow-1 {
  width: 300px;
  height: 300px;
  background: rgba(123, 147, 219, 0.2);
  top: -60px;
  left: -40px;
  animation: accGlowA 6s ease-in-out infinite alternate;
}
.acc-glow-2 {
  width: 280px;
  height: 280px;
  background: rgba(213, 200, 240, 0.2);
  bottom: -60px;
  right: -40px;
  animation: accGlowB 6s ease-in-out infinite alternate;
}
@keyframes accGlowA {
  0% { transform: translate(0, 0) scale(1); opacity: 0.6; }
  100% { transform: translate(30px, 20px) scale(1.2); opacity: 1; }
}
@keyframes accGlowB {
  0% { transform: translate(0, 0) scale(1); opacity: 0.6; }
  100% { transform: translate(-30px, -20px) scale(1.2); opacity: 1; }
}

/* 手风琴主体 */
.accordion {
  display: flex;
  width: 100%;
  height: 300px;
  gap: 10px;
  position: relative;
  z-index: 1;
}

.accordion-item {
  position: relative;
  flex: 0 0 72px;
  cursor: pointer;
  border-radius: 28px;
  overflow: hidden;
  transition: all 0.65s cubic-bezier(0.23, 1, 0.32, 1);
  display: flex;
  align-items: stretch;
  border: 1.5px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 8px 32px rgba(74, 90, 138, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

/* 像素风手风琴面板：直角 + 像素阶梯边框 */
.pixel-acc .accordion-item,
.accordion-item.pixel-acc {
  border-radius: 6px;
  border: 3px solid var(--pixel-dark);
  box-shadow:
    0 0 0 2px var(--pixel-ice),
    0 0 0 4px var(--pixel-blue),
    0 8px 32px rgba(74, 90, 138, 0.2);
}
.pixel-acc .accordion-item.active,
.accordion-item.pixel-acc.active {
  border-radius: 6px;
  border: 3px solid var(--pixel-dark);
  box-shadow:
    0 0 0 2px var(--pixel-ice),
    0 0 0 4px var(--pixel-blue),
    0 12px 48px rgba(123, 147, 219, 0.3),
    0 0 60px rgba(123, 147, 219, 0.15);
}
.pixel-acc .accordion-item:not(.active):hover,
.accordion-item.pixel-acc:not(.active):hover {
  box-shadow:
    0 0 0 2px var(--pixel-ice),
    0 0 0 4px var(--pixel-blue),
    0 12px 36px rgba(74, 90, 138, 0.25);
}

/* 面板背景：随机歌手头像 + 高透明度，不影响内容 */
.ai-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  transition: opacity 0.65s ease;
}

/* 各面板个性渐变基色（叠加在头像之上，保证可读性） */
.acc-config { background: linear-gradient(160deg, #f0e6ff 0%, #e8f0ff 40%, #ffecf5 100%); }
.acc-sign { background: linear-gradient(160deg, #fff5e6 0%, #fff0f5 40%, #ffe8ec 100%); }
.acc-draw { background: linear-gradient(160deg, #e6f7ff 0%, #f0e6ff 40%, #e6fff0 100%); }
.acc-singer { background: linear-gradient(160deg, #fff0f5 0%, #fff5e6 40%, #f5e6ff 100%); }

/* 激活状态 */
.accordion-item.active {
  flex: 1;
  border-color: rgba(255, 255, 255, 0.9);
  box-shadow:
    0 12px 48px rgba(123, 147, 219, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    0 0 60px rgba(123, 147, 219, 0.12);
}

.acc-config.active {
  box-shadow: 0 12px 48px rgba(123, 147, 219, 0.28), inset 0 1px 0 rgba(255,255,255,0.95), 0 0 60px rgba(123,147,219,0.15);
}
.acc-sign.active {
  box-shadow: 0 12px 48px rgba(255, 200, 150, 0.28), inset 0 1px 0 rgba(255,255,255,0.95), 0 0 60px rgba(255,200,150,0.15);
}
.acc-draw.active {
  box-shadow: 0 12px 48px rgba(150, 200, 255, 0.28), inset 0 1px 0 rgba(255,255,255,0.95), 0 0 60px rgba(150,200,255,0.15);
}
.acc-singer.active {
  box-shadow: 0 12px 48px rgba(255, 180, 200, 0.28), inset 0 1px 0 rgba(255,255,255,0.95), 0 0 60px rgba(255,180,200,0.15);
}

/* ===== 左侧竖条 ===== */
.ai-side {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 72px;
  min-width: 72px;
  gap: 8px;
  padding: 16px 0 0;
  position: relative;
  z-index: 2;
  flex-shrink: 0;
}

.ai-badge {
  font-size: 28px;
  filter: drop-shadow(0 3px 8px rgba(139, 181, 232, 0.4));
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  line-height: 1;
}

.accordion-item.active .ai-badge {
  transform: scale(1.15);
}

.ai-vertical-tit {
  font-size: 11px;
  font-weight: 700;
  color: var(--text-secondary);
  letter-spacing: 2px;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.accordion-item.active .ai-vertical-tit {
  opacity: 0;
}

/* ===== 展开内容区（居中） ===== */
.accordion-item .ai-content {
  position: relative;
  z-index: 2;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px 24px;
  opacity: 0;
  transform: translateX(-12px);
  transition: opacity 0.4s ease 0.15s, transform 0.5s cubic-bezier(0.23, 1, 0.32, 1) 0.15s;
  pointer-events: none;
  overflow: hidden;
  min-width: 0;
}

.accordion-item.active .ai-content {
  opacity: 1;
  transform: translateX(0);
  pointer-events: auto;
}

.ai-content-inner {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.ai-head {
  margin-bottom: 8px;
  text-align: center;
}

.ai-kicker {
  font-size: 10px;
  font-weight: 600;
  color: var(--dream-accent);
  letter-spacing: 3px;
  text-transform: uppercase;
  opacity: 0.7;
  display: block;
  margin-bottom: 4px;
}

.ai-tit {
  font-family: 'ZCOOL KuaiLe', cursive;
  font-size: 24px;
  color: var(--pixel-dark);
  margin: 0;
  letter-spacing: 2px;
  line-height: 1.3;
  text-shadow: 0 2px 8px rgba(139, 181, 232, 0.2);
}

.ai-desc {
  font-size: 13px;
  line-height: 1.7;
  color: var(--text-secondary);
  margin: 0 auto 10px;
  max-width: 380px;
  opacity: 0.85;
}

.ai-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
  justify-content: center;
}

.ai-tag {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  color: var(--dream-accent);
  background: rgba(255, 255, 255, 0.7);
  padding: 4px 14px;
  border-radius: 20px;
  box-shadow: 0 2px 6px rgba(74, 90, 138, 0.08);
  letter-spacing: 0.5px;
  backdrop-filter: blur(8px);
}

.ai-tag-soft {
  color: var(--text-secondary);
  background: rgba(255, 255, 255, 0.5);
}

/* 动作按钮 */
.ai-live-btn,
.ai-go-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 24px;
  border-radius: 24px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  white-space: nowrap;
  align-self: center;
}

.ai-live-btn {
  background: linear-gradient(135deg, #7B93DB, #9B8FD9, #B8A8E0);
  color: white;
  box-shadow: 0 6px 20px rgba(123, 147, 219, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3);
  animation: liveBtnPulse 3s ease-in-out infinite alternate;
}

@keyframes liveBtnPulse {
  0% { box-shadow: 0 6px 20px rgba(123, 147, 219, 0.4); }
  100% { box-shadow: 0 8px 28px rgba(123, 147, 219, 0.55), 0 0 40px rgba(213, 200, 240, 0.3); }
}

.ai-live-btn:hover {
  transform: translateY(-3px) scale(1.04);
  box-shadow: 0 10px 32px rgba(123, 147, 219, 0.5), 0 0 50px rgba(213, 200, 240, 0.3);
}

.ai-live-btn .pulse-dot {
  width: 10px;
  height: 10px;
  background: linear-gradient(135deg, #FF6B9D, #FFB8D0);
  border-radius: 50%;
  animation: livePulse 1s ease-in-out infinite;
  box-shadow: 0 0 12px rgba(255, 107, 157, 0.9);
}

@keyframes livePulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.6); opacity: 0.6; }
}

.ai-go-btn {
  background: rgba(255, 255, 255, 0.85);
  color: var(--pixel-dark);
  box-shadow: 0 4px 14px rgba(74, 90, 138, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
}

.ai-go-btn:hover {
  transform: translateY(-3px) scale(1.04);
  background: white;
  box-shadow: 0 8px 24px rgba(74, 90, 138, 0.18);
}

.btn-arrow {
  font-size: 12px;
  transition: transform 0.3s ease;
}

.ai-live-btn:hover .btn-arrow,
.ai-go-btn:hover .btn-arrow {
  transform: translateX(4px);
}

/* ===== 左下角标题简介（激活时显示） ===== */
.ai-footer {
  position: absolute;
  left: 24px;
  bottom: 18px;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: 0;
  transform: translateY(10px);
  transition: opacity 0.4s ease 0.35s, transform 0.5s cubic-bezier(0.23, 1, 0.32, 1) 0.35s;
  pointer-events: none;
}

.accordion-item.active .ai-footer {
  opacity: 1;
  transform: translateY(0);
}

.ai-footer-icon {
  font-size: 20px;
  filter: drop-shadow(0 2px 6px rgba(139, 181, 232, 0.4));
}

.ai-footer-text {
  font-family: 'ZCOOL KuaiLe', cursive;
  font-size: 15px;
  font-weight: 700;
  color: var(--pixel-dark);
  letter-spacing: 1px;
  text-shadow: 0 2px 8px rgba(255, 255, 255, 0.8);
}

/* 非激活面板hover效果 */
.accordion-item:not(.active):hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 36px rgba(74, 90, 138, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

/* 下滑提示 */
.scroll-hint {
  position: relative;
  z-index: 15;
  margin-top: 5vh;
  font-size: 14px;
  color: var(--text-secondary);
  letter-spacing: 2px;
  opacity: 0;
  animation: hintFade 1.2s ease-out 2.4s forwards;
  display: flex;
  align-items: center;
  gap: 8px;
}

.scroll-hint::after {
  content: '▼';
  font-size: 12px;
  animation: hintBounce 1.5s ease-in-out infinite;
}

@keyframes hintBounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(6px); }
}

@keyframes hintFade {
  0% { opacity: 0; }
  100% { opacity: 0.85; }
}

/* ================================================================
   内容区
   ================================================================ */
.content-section {
  position: relative;
  z-index: 1;
  scroll-snap-align: start;
  padding: 20px 0 0;
}

.module-anchor {
  position: relative;
  scroll-margin-top: 16px;
}

.main-container {
  position: relative;
  max-width: 1020px;
  margin: 0 auto;
  padding: 32px 24px 64px;
}

.lace-wrapper {
  position: relative;
  background: var(--glass-bg);
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  border-radius: 8px;
  padding: 4px;
  box-shadow: 0 16px 50px var(--shadow-soft), 0 6px 20px rgba(139, 181, 232, 0.18), inset 0 2px 0 rgba(255, 255, 255, 0.75), 0 0 80px var(--glow-blue);
  animation: wrapperGlowEnhanced 5s ease-in-out infinite alternate;
  border: 3px solid var(--pixel-dark);
}

@keyframes wrapperGlowEnhanced {
  0% { box-shadow: 0 16px 50px var(--shadow-soft), 0 6px 20px rgba(139, 181, 232, 0.18), inset 0 2px 0 rgba(255, 255, 255, 0.75), 0 0 80px var(--glow-blue); }
  50% { box-shadow: 0 18px 55px var(--shadow-soft), 0 8px 25px rgba(139, 181, 232, 0.22), inset 0 2px 0 rgba(255, 255, 255, 0.78), 0 0 100px var(--glow-pink); }
  100% { box-shadow: 0 18px 58px var(--shadow-soft), 0 9px 28px rgba(139, 181, 232, 0.24), inset 0 2px 0 rgba(255, 255, 255, 0.8), 0 0 110px var(--glow-blue); }
}

.lace-wrapper::before {
  content: '';
  position: absolute;
  inset: -6px;
  border-radius: 11px;
  background: linear-gradient(45deg, var(--pixel-pink), var(--pixel-lavender), var(--pixel-blue), var(--dream-mint), var(--pixel-pale), var(--pixel-pink));
  background-size: 500% 500%;
  animation: rainbowBorderEnhanced 8s ease infinite;
  z-index: -1;
  opacity: 0.65;
}

@keyframes rainbowBorderEnhanced {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.inner-content {
  background: var(--glass-strong);
  backdrop-filter: blur(40px);
  -webkit-backdrop-filter: blur(40px);
  border-radius: 8px;
  padding: 40px 36px;
  position: relative;
  border: 3px solid var(--pixel-dark);
  box-shadow: inset 0 0 0 2px var(--pixel-ice);
  overflow: hidden;
}

.inner-content::before,
.inner-content::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: 20px;
  background-image: radial-gradient(circle at 10px 20px, var(--dream-primary) 8px, transparent 8.5px), radial-gradient(circle at 50px 20px, var(--dream-pink) 7px, transparent 7.5px), radial-gradient(circle at 90px 20px, var(--pixel-blue) 6px, transparent 6.5px);
  background-size: 100px 20px;
  background-repeat: repeat-x;
  opacity: 0.55;
  animation: laceWaveEnhanced 5s ease-in-out infinite;
}

.inner-content::before { top: 0; }
.inner-content::after { bottom: 0; transform: rotate(180deg); animation-delay: 2.5s; }

@keyframes laceWaveEnhanced {
  0%, 100% { opacity: 0.45; }
  50% { opacity: 0.65; }
}

/* 跨设备分享链接 - 固定右下角 */
.share-link-btn {
  position: fixed;
  right: 16px;
  bottom: 20px;
  z-index: 50;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--glass-strong);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  color: var(--text-secondary);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  border: 1.5px solid var(--glass-border);
  cursor: pointer;
  transition: var(--transition-bounce);
  font-family: inherit;
  box-shadow: 0 2px 8px var(--shadow-soft);
  line-height: 1;
}

.share-link-btn:hover {
  background: linear-gradient(135deg, var(--dream-accent), var(--dream-lavender));
  color: white;
  border-color: transparent;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(123, 147, 219, 0.35);
}

.share-phone-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

/* ================================================================
   Toast 提示
   ================================================================ */
.toast {
  position: fixed;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%) translateY(25px);
  background: linear-gradient(135deg, var(--dream-deep), var(--dream-accent));
  color: white;
  padding: 14px 32px;
  border-radius: 28px;
  font-size: 15px;
  z-index: 200;
  opacity: 0;
  transition: all 0.4s ease;
  box-shadow: 0 8px 28px rgba(74, 90, 138, 0.4), 0 0 40px var(--glow-blue);
  pointer-events: none;
}

.toast.show {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

/* ================================================================
   歌单弹窗
   ================================================================ */
.songlist-overlay {
  position: fixed;
  inset: 0;
  background: rgba(74, 90, 138, 0.35);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  /* 需高于顶部固定栏(.top-bar, z-index:1000)，避免被其遮挡 */
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.4s ease;
  overflow-y: auto;
}

.songlist-overlay.active {
  opacity: 1;
  pointer-events: auto;
}

.songlist-modal {
  background: var(--glass-strong);
  backdrop-filter: blur(40px);
  -webkit-backdrop-filter: blur(40px);
  border-radius: 8px;
  width: 100%;
  max-width: 620px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 80px rgba(74, 90, 138, 0.35), 0 0 100px var(--glow-blue);
  transform: scale(0.88) translateY(30px);
  transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
  border: 3px solid var(--pixel-dark);
  position: relative;
}

.songlist-modal::before {
  content: '';
  position: absolute;
  inset: -6px;
  border-radius: 11px;
  background: linear-gradient(45deg, var(--pixel-pink), var(--pixel-lavender), var(--pixel-blue), var(--dream-mint));
  background-size: 400% 400%;
  animation: songlistRainbowBorder 6s ease infinite;
  z-index: -1;
  opacity: 0.6;
}

@keyframes songlistRainbowBorder {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

.songlist-overlay.active .songlist-modal {
  transform: scale(1) translateY(0);
}

.songlist-header {
  padding: 24px 28px 20px;
  text-align: center;
  position: relative;
  border-bottom: 1px solid var(--glass-border);
  background: linear-gradient(180deg, var(--glass-bg), transparent);
}

.songlist-close-btn {
  position: absolute;
  top: 18px;
  right: 18px;
  width: 36px;
  height: 36px;
  border: none;
  background: var(--glass-bg);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  transition: var(--transition-bounce);
  font-size: 20px;
  box-shadow: 0 2px 8px var(--shadow-soft);
  font-family: inherit;
}

.songlist-close-btn:hover {
  background: var(--dream-pink);
  color: var(--dream-deep);
  transform: rotate(90deg) scale(1.1);
  box-shadow: 0 4px 12px var(--glow-pink);
}

.songlist-singer-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  margin: 0 auto 12px;
  overflow: hidden;
  border: 4px solid white;
  box-shadow: 0 6px 20px var(--shadow-soft), 0 0 30px var(--glow-pink);
  background: linear-gradient(135deg, var(--dream-light), var(--dream-primary), var(--dream-lavender));
}

.songlist-singer-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.songlist-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: white;
  font-family: 'ZCOOL KuaiLe', cursive;
}

.songlist-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--pixel-dark);
  margin-bottom: 6px;
}

.songlist-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
}

/* 歌单搜索栏 */
.songlist-search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: var(--glass-bg);
  border-bottom: 1px solid var(--glass-border);
}

.songlist-search-icon {
  color: var(--text-light);
  flex-shrink: 0;
}

.songlist-search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: var(--text-primary);
  font-family: inherit;
  padding: 6px 0;
}

.songlist-search-input::placeholder {
  color: var(--text-light);
  opacity: 0.75;
}

.songlist-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 20px 24px;
  scrollbar-width: thin;
  scrollbar-color: var(--dream-primary) transparent;
}

.songlist-body::-webkit-scrollbar {
  width: 8px;
}

.songlist-body::-webkit-scrollbar-track {
  background: transparent;
}

.songlist-body::-webkit-scrollbar-thumb {
  background: var(--dream-primary);
  border-radius: 4px;
}

.song-group-header {
  font-size: 15px;
  font-weight: 700;
  color: var(--dream-accent);
  padding: 14px 10px 8px;
  position: sticky;
  top: 0;
  background: var(--glass-strong);
  backdrop-filter: blur(15px);
  z-index: 1;
  border-bottom: 2px solid var(--glass-border);
  text-shadow: 0 1px 3px rgba(139, 181, 232, 0.4);
}

.song-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: var(--radius-md);
  transition: all 0.25s ease;
  cursor: default;
  animation: songItemSlideEnhanced 0.4s ease-out backwards;
}

@keyframes songItemSlideEnhanced {
  from { opacity: 0; transform: translateX(-15px) scale(0.95); }
  to { opacity: 1; transform: translateX(0) scale(1); }
}

.song-item:hover {
  background: linear-gradient(90deg, var(--glass-bg), transparent);
  transform: translateX(5px);
}

.song-item .song-index {
  font-size: 13px;
  color: var(--text-light);
  min-width: 28px;
  text-align: right;
  font-weight: 500;
}

.song-item .song-title {
  flex: 1;
  font-size: 15px;
  color: var(--text-primary);
  line-height: 1.5;
}

.song-item .song-note {
  font-size: 12px;
  color: var(--text-secondary);
  background: linear-gradient(135deg, var(--dream-pink), var(--dream-lavender));
  padding: 2px 10px;
  border-radius: 12px;
  margin-left: 6px;
  white-space: nowrap;
  box-shadow: 0 2px 6px var(--glow-pink);
}

.song-item .song-image {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid var(--glass-border);
  box-shadow: 0 2px 8px var(--shadow-soft);
}

.songlist-footer {
  padding: 16px 28px;
  text-align: center;
  font-size: 14px;
  color: var(--text-secondary);
  border-top: 1px solid var(--glass-border);
  background: var(--glass-bg);
}

/* ================================================================
   响应式
   ================================================================ */
@media (max-width: 768px) {
  .pixel-border-left,
  .pixel-border-right {
    display: none;
  }

  .main-container {
    padding: 16px 12px 48px;
  }

  .inner-content {
    padding: 24px 16px;
  }

  .hero-section {
    padding: 24px 16px;
  }

  /* 歌单弹窗在移动端全屏展示 */
  .songlist-overlay {
    padding: 0;
  }

  .songlist-modal {
    max-width: 100%;
    max-height: 100vh;
    height: 100vh;
    border-radius: 0;
    border: none;
  }

  .songlist-modal::before {
    display: none;
  }

  .songlist-header {
    padding: 20px 20px 16px;
  }

  .songlist-body {
    padding: 8px 12px 20px;
  }

  .lace-corner {
    width: 85px;
    height: 85px;
  }

  .center-star {
    font-size: 75px;
  }

  /* 标题在窄屏允许换行，避免横向溢出 */
  .dream-title {
    margin-top: -2vh;
  }

  .dream-title h1 {
    font-size: clamp(22px, 6.5vw, 34px);
    letter-spacing: 2px;
    white-space: normal;
    line-height: 1.5;
    padding: 0 18px;
  }

  .dream-title h1::before,
  .dream-title h1::after {
    display: none;
  }

  .dream-subtitle {
    letter-spacing: 2px;
    margin-top: 10px;
  }

  /* 手风琴在移动端适配：垂直布局，激活面板高度自适应 */
  .accordion-shell {
    padding: 0;
  }

  .accordion {
    flex-direction: column;
    height: auto;
    gap: 8px;
  }

  .accordion-item {
    flex: 0 0 52px;
  }

  .accordion-item.active {
    flex: 0 0 auto;
    min-height: 200px;
  }

  /* 移动端折叠时竖排文字改为隐藏，横排时会倒置 */
  .ai-vertical-tit {
    display: none;
  }

  .ai-side {
    width: 52px;
    min-width: 52px;
    padding: 10px 0 0;
  }

  .ai-badge {
    font-size: 20px;
  }

  .accordion-item .ai-content {
    padding: 12px 12px;
  }

  .ai-tit {
    font-size: 18px;
  }

  .ai-desc {
    font-size: 12px;
    max-width: 100%;
  }

  .ai-meta {
    margin-bottom: 8px;
    gap: 6px;
  }

  .ai-live-btn,
  .ai-go-btn {
    padding: 8px 18px;
    font-size: 12px;
  }

  .ai-footer {
    display: none;
  }

  .ai-footer-text {
    font-size: 12px;
  }

  /* 分享按钮位置调整 */
  .share-link-btn {
    right: 12px;
    bottom: 16px;
    padding: 6px 12px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .dream-title h1 {
    font-size: 30px;
  }

  .dream-subtitle {
    font-size: 14px;
  }

  .accordion {
    height: auto;
    gap: 6px;
  }

  .accordion-item {
    flex: 0 0 46px;
  }

  .accordion-item.active {
    min-height: 180px;
  }

  .ai-side {
    width: 44px;
    min-width: 44px;
  }

  .ai-tit {
    font-size: 16px;
  }

  .accordion-item .ai-content {
    padding: 8px 10px;
  }

  .ai-footer {
    display: none;
  }
}
</style>